import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  InvoiceableReceiptLine,
  PaginatedResult,
  PurchaseInvoiceDetail,
  PurchaseInvoiceStatus,
  PurchaseInvoiceSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';
import { purchaseOrderKeys } from './purchase-orders.api';
import { purchaseReceiptKeys } from './purchase-receipts.api';

export interface PurchaseInvoiceListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  supplierId?: string;
  branchId?: string;
  status?: PurchaseInvoiceStatus | '';
  dateFrom?: string;
  dateTo?: string;
  dueFrom?: string;
  dueTo?: string;
}

export interface InvoiceableLineParams {
  page?: number;
  pageSize?: number;
  supplierId?: string;
  branchId?: string;
  purchaseOrderId?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** One delivered line as it is billed. No total is ever sent: the server prices the invoice. */
export interface PurchaseInvoiceLineInput {
  purchaseReceiptLineId: string;
  quantityInvoiced: number;
  billedUnitCostMinor: number;
  taxProfileId?: string;
}

export const purchaseInvoiceKeys = {
  all: ['purchase-invoices'] as const,
  list: (params: PurchaseInvoiceListParams) =>
    [...purchaseInvoiceKeys.all, 'list', params] as const,
  detail: (id: string) => [...purchaseInvoiceKeys.all, 'detail', id] as const,
  forOrder: (purchaseOrderId: string) =>
    [...purchaseInvoiceKeys.all, 'for-order', purchaseOrderId] as const,
  invoiceable: (params: InvoiceableLineParams) =>
    [...purchaseInvoiceKeys.all, 'invoiceable', params] as const,
};

export function usePurchaseInvoicesQuery(
  params: PurchaseInvoiceListParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: purchaseInvoiceKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<PurchaseInvoiceSummary>>('/purchasing/invoices', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.search?.trim() ? { search: params.search.trim() } : {}),
          ...(params.supplierId ? { supplierId: params.supplierId } : {}),
          ...(params.branchId ? { branchId: params.branchId } : {}),
          ...(params.status ? { status: params.status } : {}),
          ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params.dateTo ? { dateTo: params.dateTo } : {}),
          ...(params.dueFrom ? { dueFrom: params.dueFrom } : {}),
          ...(params.dueTo ? { dueTo: params.dueTo } : {}),
        },
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/** Every supplier invoice that bills a delivery against one order. */
export function usePurchaseOrderInvoicesQuery(purchaseOrderId: string | undefined) {
  return useQuery({
    queryKey: purchaseInvoiceKeys.forOrder(purchaseOrderId ?? ''),
    queryFn: () =>
      api.get<PaginatedResult<PurchaseInvoiceSummary>>(
        `/purchasing/orders/${purchaseOrderId ?? ''}/invoices`,
        { params: { pageSize: 50 } },
      ),
    enabled: Boolean(purchaseOrderId),
  });
}

export function usePurchaseInvoiceQuery(id: string | undefined) {
  return useQuery({
    queryKey: purchaseInvoiceKeys.detail(id ?? ''),
    queryFn: () => api.get<PurchaseInvoiceDetail>(`/purchasing/invoices/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/**
 * What has been received and not yet billed.
 *
 * Needs a supplier before it asks: an invoice covers one supplier, so a list spanning all of them
 * is a list of rows that can never go on the same document.
 */
export function useInvoiceableLinesQuery(
  params: InvoiceableLineParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: purchaseInvoiceKeys.invoiceable(params),
    queryFn: () =>
      api.get<PaginatedResult<InvoiceableReceiptLine>>('/purchasing/invoiceable-receipts', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          ...(params.supplierId ? { supplierId: params.supplierId } : {}),
          ...(params.branchId ? { branchId: params.branchId } : {}),
          ...(params.purchaseOrderId ? { purchaseOrderId: params.purchaseOrderId } : {}),
          ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params.dateTo ? { dateTo: params.dateTo } : {}),
        },
      }),
    placeholderData: (previous) => previous,
    enabled,
  });
}

/**
 * Records a supplier's invoice.
 *
 * One key per attempt: a double click or a retry after a lost response records the supplier's
 * document once. The order's and the delivery's own reads are refreshed as well, because what each
 * of them has left to bill has just changed.
 */
export function usePostPurchaseInvoiceMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<
    PurchaseInvoiceDetail,
    {
      supplierInvoiceNumber: string;
      invoiceDate: string;
      lines: PurchaseInvoiceLineInput[];
    }
  >((payload, config) => api.post<PurchaseInvoiceDetail>('/purchasing/invoices', payload, config), {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: purchaseInvoiceKeys.all });
      void queryClient.invalidateQueries({ queryKey: purchaseReceiptKeys.all });
      void queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
    },
  });
}

/** Voids one, with a reason. The document stays; the obligation and the billed quantity go. */
export function useVoidPurchaseInvoiceMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<PurchaseInvoiceDetail, { id: string; reason: string }>(
    ({ id, reason }, config) =>
      api.post<PurchaseInvoiceDetail>(`/purchasing/invoices/${id}/void`, { reason }, config),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: purchaseInvoiceKeys.all });
        void queryClient.invalidateQueries({ queryKey: purchaseReceiptKeys.all });
        void queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      },
    },
  );
}
